export const VALID_CATEGORIES = ['top', 'bottom', 'dress', 'shoes', 'accessories'] as const;
export type ClothingCategory = typeof VALID_CATEGORIES[number];

const CATEGORY_MAP: Record<string, ClothingCategory> = {
  shirt: 'top', blouse: 'top', sweater: 'top', 't-shirt': 'top', tank: 'top',
  cardigan: 'top', jacket: 'top', blazer: 'top', coat: 'top', hoodie: 'top',
  outerwear: 'top', vest: 'top', crop: 'top', turtleneck: 'top', polo: 'top',
  pants: 'bottom', jeans: 'bottom', skirt: 'bottom', shorts: 'bottom',
  leggings: 'bottom', trousers: 'bottom', chinos: 'bottom',
  dress: 'dress', gown: 'dress', romper: 'dress', jumpsuit: 'dress',
  shoes: 'shoes', boots: 'shoes', sneakers: 'shoes', heels: 'shoes',
  sandals: 'shoes', flats: 'shoes', loafers: 'shoes', footwear: 'shoes',
  bag: 'accessories', belt: 'accessories', hat: 'accessories', scarf: 'accessories',
  jewelry: 'accessories', sunglasses: 'accessories', watch: 'accessories',
  necklace: 'accessories', earrings: 'accessories', bracelet: 'accessories',
  purse: 'accessories', clutch: 'accessories', tote: 'accessories',
};

export function normalizeCategory(raw: string): ClothingCategory {
  const lower = (raw || '').toLowerCase().trim();
  if (VALID_CATEGORIES.includes(lower as any)) return lower as ClothingCategory;
  if (CATEGORY_MAP[lower]) return CATEGORY_MAP[lower];
  for (const [keyword, mapped] of Object.entries(CATEGORY_MAP)) {
    if (lower.includes(keyword)) return mapped;
  }
  return 'top';
}

// ---------------------------------------------------------------------------
// Subcategories — a second, optional level under each top-level category.
// The lists below are the canonical taxonomy (single source of truth); the
// item-form pickers and the Digital Closet filters are all driven from here.
// ---------------------------------------------------------------------------

export const SUBCATEGORIES: Record<ClothingCategory, string[]> = {
  top: ['Shirt', 'Blouse', 'T-Shirt', 'Sweater', 'Cardigan', 'Jacket', 'Blazer', 'Coat', 'Tank'],
  bottom: ['Jeans', 'Pants', 'Shorts', 'Skirt'],
  dress: ['Dress', 'Jumpsuit'],
  shoes: ['Sneakers', 'Boots', 'Heels', 'Flats', 'Sandals'],
  accessories: ['Bag', 'Belt', 'Hat', 'Jewelry', 'Sunglasses'],
};

/** The subcategory options for a (possibly messy) category string. */
export function subcategoriesFor(rawCategory: string): string[] {
  return SUBCATEGORIES[normalizeCategory(rawCategory)] ?? [];
}

// Keyword → canonical subcategory, scoped per top-level category. Used to
// best-effort infer a subcategory from an item's name (backfill + form suggest).
// Keys are matched as substrings against the lowercased name; first hit wins,
// so order more specific terms before broader ones.
const SUBCATEGORY_KEYWORDS: Record<ClothingCategory, [string, string][]> = {
  top: [
    ['t-shirt', 'T-Shirt'], ['tshirt', 'T-Shirt'], ['tee', 'T-Shirt'],
    ['blouse', 'Blouse'],
    ['button', 'Shirt'], ['oxford', 'Shirt'], ['flannel', 'Shirt'], ['shirt', 'Shirt'],
    ['cardigan', 'Cardigan'],
    ['hoodie', 'Sweater'], ['sweatshirt', 'Sweater'], ['pullover', 'Sweater'],
    ['knit', 'Sweater'], ['jumper', 'Sweater'], ['turtleneck', 'Sweater'], ['sweater', 'Sweater'],
    ['blazer', 'Blazer'],
    ['trench', 'Coat'], ['parka', 'Coat'], ['overcoat', 'Coat'], ['coat', 'Coat'],
    ['jacket', 'Jacket'], ['bomber', 'Jacket'], ['denim jacket', 'Jacket'], ['puffer', 'Jacket'],
    ['camisole', 'Tank'], ['cami', 'Tank'], ['tank', 'Tank'],
  ],
  bottom: [
    ['denim', 'Jeans'], ['jean', 'Jeans'],
    ['short', 'Shorts'],
    ['skirt', 'Skirt'],
    ['legging', 'Pants'], ['trouser', 'Pants'], ['chino', 'Pants'], ['slack', 'Pants'], ['pant', 'Pants'],
  ],
  dress: [
    ['jumpsuit', 'Jumpsuit'], ['romper', 'Jumpsuit'], ['playsuit', 'Jumpsuit'],
    ['gown', 'Dress'], ['dress', 'Dress'],
  ],
  shoes: [
    ['sneaker', 'Sneakers'], ['trainer', 'Sneakers'],
    ['boot', 'Boots'], ['bootie', 'Boots'],
    ['heel', 'Heels'], ['pump', 'Heels'], ['stiletto', 'Heels'],
    ['sandal', 'Sandals'], ['slide', 'Sandals'], ['espadrille', 'Sandals'],
    ['loafer', 'Flats'], ['ballet', 'Flats'], ['flat', 'Flats'], ['mule', 'Flats'],
  ],
  accessories: [
    ['sunglass', 'Sunglasses'], ['glasses', 'Sunglasses'], ['eyewear', 'Sunglasses'],
    ['belt', 'Belt'],
    ['hat', 'Hat'], ['cap', 'Hat'], ['beanie', 'Hat'],
    ['necklace', 'Jewelry'], ['earring', 'Jewelry'], ['bracelet', 'Jewelry'],
    ['ring', 'Jewelry'], ['watch', 'Jewelry'], ['jewel', 'Jewelry'],
    ['bag', 'Bag'], ['purse', 'Bag'], ['tote', 'Bag'], ['clutch', 'Bag'], ['backpack', 'Bag'],
  ],
};

/**
 * Best-effort infer a subcategory from an item's name, scoped to its category.
 * Returns null when nothing matches confidently (item stays uncategorized).
 */
export function inferSubcategory(name: string, rawCategory: string): string | null {
  const lower = (name || '').toLowerCase();
  if (!lower) return null;
  const cat = normalizeCategory(rawCategory);
  for (const [keyword, sub] of SUBCATEGORY_KEYWORDS[cat]) {
    if (lower.includes(keyword)) return sub;
  }
  return null;
}
